<template>
    <div>
        <!-- Header (visible in steps 1-3) -->
        <template v-if="step >= 1 && step < 4">
            <h2 class="tw:text-2xl tw:font-bold tw:text-slate-900 tw:m-0">{{ stepTitles }}</h2>
            <h3 class="tw:text-sm tw:font-semibold tw:uppercase tw:text-slate-600 tw:m-0 tw:mt-1">{{ pedidoView?.evento?.nombre }}</h3>
        </template>
        <Stepper
            v-if="step < 4"
            data-testid="cambio-datos-stepper"
            :steps="stepperLabels"
            :current-step="step"
            class="tw:my-4"
        />
        <div v-if="step >= 1 && step < 4" class="tw:flex tw:items-center tw:gap-1 tw:bg-blue-50 tw:border tw:border-blue-200 tw:rounded-xl tw:p-4 tw:mb-6">
            <i class="material-icons tw:text-blue-500 tw:mt-0.5">info</i>
            <div class="tw:space-y-2">
                <p class="tw:text-sm tw:text-blue-800 tw:m-0">{{ stepBanners }}</p>
                <p v-if="step === 2" class="tw:text-sm tw:text-blue-800 tw:m-0 tw:font-medium">
                    {{ costePorCambio > 0 ? $t('perfil.cambio_datos.coste_por_cambio', { coste: formatearPrecio(costePorCambio) }) : $t('perfil.cambio_datos.sin_coste') }}
                </p>
            </div>
        </div>
        <PasoSeleccion
            v-if="step === 1"
            :pedido="pedido"
            @volver="handleVolver"
            @continuar="handleContinuarPaso1"
        />
        <PasoFormulario
            v-else-if="step === 2"
            :entradas-seleccionadas="entradasSeleccionadas"
            :datos-nuevos="datosNuevos"
            :coste-por-cambio="costePorCambio"
            :total-cambios="totalCambios"
            :loading="loading"
            :error="error"
            :is-form-valid="isFormValid"
            :es-duplicado="esDuplicado"
            :pedido="pedidoView"
            :termino-entrada="terminoEntrada"
            :termino-entradas="terminoEntradas"
            :teatro="teatro"
            @update-dato="onUpdateDato"
            @volver="step = 1"
            @continuar="guardarDatos"
        />
        <PasoResumen
            v-else-if="step === 3"
            :pedido="pedido"
            :entradas-seleccionadas="entradasSeleccionadas"
            :datos-nuevos="datosNuevos"
            :datos-compra="datosCompra"
            :total-cambios="totalCambios"
            @volver="step = 2"
            @cancelar="handleCancelar"
            @pago-completado="handlePagoCompletado"
        />
        <PasoExito
            v-else-if="step === 4"
            :pedido="pedidoView"
            :entradas-seleccionadas="entradasSeleccionadas"
            @volver="handleVolverExito"
        />
    </div>
</template>

<script setup>
    import { onActivated, computed, getCurrentInstance } from 'vue';
    import store from '@/store';
    import { useCambiarDatos } from '@/composables/front/perfil/useCambiarDatos';
    import { useFilters } from '@/composables/front/formatters/useFilters';
    import Stepper from '@/modules/front/ui/Stepper.vue';
    import PasoSeleccion from '../components/CambiarDatos/PasoSeleccion.vue';
    import PasoFormulario from '../components/CambiarDatos/PasoFormulario.vue';
    import PasoResumen from '../components/CambiarDatos/PasoResumen.vue';
    import PasoExito from '../components/CambiarDatos/PasoExito.vue';

    const instance = getCurrentInstance();
    const $t = instance.proxy.$t.bind(instance.proxy);
    const teatro = computed(() => store.getters.getTeatro);

    const props = defineProps({
        pedido: { type: Object, required: true },
    });

    const emit = defineEmits(['volver', 'cambios-guardados']);

    const {
        step,
        pedido: pedidoView,
        entradasSeleccionadas,
        datosNuevos,
        datosCompra,
        loading,
        error,
        isFormValid,
        esDuplicado,
        costePorCambio,
        totalCambios,
        terminoEntrada,
        terminoEntradas,
        iniciarFlujo,
        seleccionarEntradas,
        actualizarDato,
        guardarDatos,
        obtenerDatosActualizados,
        reset,
    } = useCambiarDatos();

    const { formatearPrecio } = useFilters();

    onActivated(() => iniciarFlujo(props.pedido));

    const handleVolver = () => {
        reset();
        emit('volver');
    };

    const handleContinuarPaso1 = entradas => {
        seleccionarEntradas(entradas);
        step.value = 2;
    };

    const onUpdateDato = ({ ticketId, campo, valor }) => {
        actualizarDato(ticketId, campo, valor);
    };

    const handleCancelar = () => {
        reset();
        emit('volver');
    };

    const handlePagoCompletado = () => {
        const actualizados = obtenerDatosActualizados();
        actualizados.forEach(a => {
            const target = pedidoView.value.entradas.find(e => e.id === a.id);
            if (target) {
                target.user_nombre = a.user_nombre;
                target.user_apellidos = a.user_apellidos;
                target.user_dni = a.user_dni;
            }
        });
        step.value = 4;
    };

    const handleVolverExito = () => {
        emit('cambios-guardados');
        reset();
    };

    const stepperLabels = computed(() => [
        $t('perfil.cambio_datos.step_seleccion'),
        $t('perfil.datos'),
        $t('perfil.pago'),
    ]);

    const stepTitles = computed(() => {
        const titles = ['seleccionar_titulo', 'introducir_titulo', 'resumen_titulo'];
        const idx = step.value - 1;
        return idx >= 0 && idx < titles.length ? $t(`perfil.cambio_datos.${titles[idx]}`) : '';
    });

    const stepBanners = computed(() => {
        const banners = ['instruccion_seleccion', 'instruccion_formulario', 'instruccion_resumen'];
        const idx = step.value - 1;
        return idx >= 0 && idx < banners.length ? $t(`perfil.cambio_datos.${banners[idx]}`) : '';
    });
</script>
